import request, { downloadFile } from 'tools/request'
export const SET_CLIENTS_LIST = 'SET_CLIENTS_LIST'
export const SET_CLIENT = 'SET_CLIENT'
export const RESET_FORM = 'RESET_FORM'

export const dispatchClientsList = (data, headers) => ({
    type: SET_CLIENTS_LIST,
    data,
    headers
})

export const dispatchClientById = (data) => ({
    type: SET_CLIENT,
    data
})

export const resetClientEditForm = () => ({
    type: RESET_FORM
})

export const getClientsList = (filters = {}, pagination = {}, getAllData) => async (dispatch) => {
    const query = {
        ...filters
    }

    // When getAllData is set the caller wants every client (e.g. to fill a
    // dropdown), not just the first page. The backend caps each page at its
    // default limit, so page through all of them and concatenate the results.
    if (getAllData) {
        const pageLimit = 200
        const maxPages = 100 // safety guard against an unbounded loop
        let pageNum = 0
        let allClients = []
        let firstRes = null

        while (pageNum < maxPages) {
            const res = await request(
                'clients',
                'GET',
                null,
                true,
                {
                    'x-pagination-num': pageNum,
                    'x-pagination-limit': pageLimit
                },
                query
            )
            if (!firstRes) firstRes = res
            const batch = Array.isArray(res.response) ? res.response : []
            allClients = allClients.concat(batch)

            const totalRecords = Number(res.headers?.['x-pagination-total-records'])
            const reachedTotal = Number.isFinite(totalRecords) && allClients.length >= totalRecords
            if (batch.length < pageLimit || reachedTotal) break
            pageNum += 1
        }

        dispatch(dispatchClientsList(allClients, firstRes?.headers))
        return { ...firstRes, response: allClients }
    }

    const paginationProps = {
        'x-pagination-num': pagination.page || 0,
        'x-pagination-limit': pagination.limit || 10
    }
    const res = await request(
        'clients',
        'GET',
        null,
        true,
        paginationProps,
        query
    )
    dispatch(dispatchClientsList(res.response, res.headers))
    return res
}

export const getClientById = (clientId) => async (dispatch) => {
    const res = await request(`clients/${clientId}`, 'GET')
    dispatch(dispatchClientById(res))
    return res
}

export const createClient = (payLoad) => async () => {
    const res = await request('clients', 'POST', payLoad)
    return res
}

export const getClientPortals = () => {
    return request('clients/portals', 'GET')
}

export const updateClient = (payLoad) => async () => {
    const res = await request(`clients/${payLoad.uuid}`, 'PATCH', payLoad)
    return res
}

export const downloadReport = () => async () => {
    const res = await downloadFile(`billing/getReport`, 'GET', null, 'download.csv')
    return res
}

export const updateNotificationDetails = (data)  => {
    const res = request('notification-settings', 'PATCH', data)
    return res
}

export const getNotificationDetails = () => {
    const res = request('notification-settings', 'GET')
    return res
}

// Saved defaults for the applicant decline email (flag APPLICATION_DECLINE_EMAIL).
export const getDeclineEmailSettings = () => request('decline-email-settings', 'GET')

export const updateDeclineEmailSettings = (data) => request('decline-email-settings', 'PUT', data)

export const previewDeclineEmail = (data) => request('decline-email-settings/preview', 'POST', data)

// One of the application emails for a portal, rendered with the product name being typed (not saved)
export const previewPortalEmail = (clientUuid, clientFormConfigId, email, productName) =>
    request(`clients/${clientUuid}/portal/${clientFormConfigId}/email-preview`, 'GET', null, false, {}, { email, productName }, false)

export const cancelSubscription = () => {
  return request(`stripe/write/cancel-subscription`, 'POST', null)
}

export const subscriptionDetails = () => {
  return request(`stripe/read/subscription-details`, 'GET')
}

export const switchSubscription = (data) => {
  return request(`stripe/write/switch-subscription`, 'POST', data)
}

export const buyCredits = (data) => {
    return request(`stripe/write/buy-credits`, 'POST', data)
}

export const addCredits = (data) => {
    return request(`stripe/write/add-credits`, 'POST', data)
}

export const getCreditPackages = (pageLimit = 5, pageNumber = 0, startDate = '', endDate = '', sortingParam = 'createdAt,desc') => {
    const headers = {
        'X-Pagination-Limit': pageLimit.toString(),
        'X-Pagination-Num': pageNumber.toString(),
        'X-Pagination-Sort': sortingParam
    };
    
    const queryParams = {};
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;
    
    return request("credit-transactions", 'GET', null, true, headers, queryParams);
}

export const getCreditCosts = () => {
    return request("credit-costs", 'GET');
}

export const getReminderConfig = (clientUuid) => {
    return request(`clients/${clientUuid}/reminder-config`, 'GET', null, false, {}, null, false)
}

export const createReminderConfig = (clientUuid, data) => {
    return request(`clients/${clientUuid}/reminder-config`, 'POST', data)
}

export const updateReminderConfig = (clientUuid, data) => {
    return request(`clients/${clientUuid}/reminder-config`, 'PATCH', data)
}

export const deleteReminderConfig = (clientUuid) => {
    return request(`clients/${clientUuid}/reminder-config`, 'DELETE', null, false, {}, null, false)
}

export const getDocusignTemplateConfig = (clientUuid, templateType) => {
    return request(`clients/${clientUuid}/docusign/templates/${templateType}`, 'GET', null, false, {}, null, false)
}

export const configureDocusignTemplate = (clientUuid, templateType, data) => {
    return request(`clients/${clientUuid}/docusign/templates/${templateType}`, 'PUT', data)
}

export const getMyClientContactInfo = () => {
    return request('clients/me/contact-info', 'GET')
}

/**
 * Two-factor requirement of the caller's own client. The backend resolves the client from
 * the token, so these never carry a client id: a rox admin editing another client has to go
 * through the feature-flag screen instead.
 */
export const getMySecuritySettings = () => {
    return request('clients/me/security-settings', 'GET')
}

export const updateMySecuritySettings = (data) => {
    return request('clients/me/security-settings', 'PATCH', data)
}

export const getMyFlaggedBusinessTypes = () => {
    return request('clients/me/flagged-business-types', 'GET')
}

export const updateMyFlaggedBusinessTypes = (data) => {
    return request('clients/me/flagged-business-types', 'PUT', data)
}

// Rox-admin only: branding and texts of a client's Plaid quickstart page. GET resolves undefined on 204 (no config).
export const getClientPlaidConfig = (clientUuid) => {
    return request(`admin/client-plaid-config/${clientUuid}`, 'GET')
}

export const saveClientPlaidConfig = (clientUuid, data) => {
    return request(`admin/client-plaid-config/${clientUuid}`, 'PUT', data)
}

export const deleteClientPlaidConfig = (clientUuid) => {
    return request(`admin/client-plaid-config/${clientUuid}`, 'DELETE')
}

// Rox-admin only: a client's Plaid bank connections, one per owner. Deleting removes the connection at Plaid and
// all of the owner's bank data in Rox; it is silent on error so the caller can react to Plaid failing (409).
export const getPlaidConnections = (clientUuid) => {
    return request(`admin/plaid-connections/${clientUuid}`, 'GET')
}

export const getPlaidConnectionDeletions = (clientUuid) => {
    return request(`admin/plaid-connections/${clientUuid}/deletions`, 'GET', undefined, false, {}, undefined, false, true)
}

export const deletePlaidConnection = (clientUuid, ownerUuid, data) => {
    return request(`admin/plaid-connections/${clientUuid}/${ownerUuid}/delete`, 'POST', data, false, {}, undefined, false, true)
}

export const updateMyClientAddressAndPhone = (data) => {
    return request('clients/me/address-phone', 'PATCH', data)
}
export const getPhoneTypes = () => {
    return request('clients/phone-types', 'GET')
}